import React, { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import qs from 'query-string';
import { useMutation, useQuery } from 'react-apollo';

import useIsNativeWebView from '../../../native-bridge/hooks/useIsWebView';
import AccountCardAlbumCustomPrintConfigurator from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator';
import FullScreenLoader from '../../../../components/global/FullScreenLoader';
import useStandaloneAlbumCustomPrintConfigurator from './hooks/useStandaloneAlbumCustomPrintConfigurator';
import {
  postNativeAppAlbumCustomPrintConfiguratorDismiss,
  postNativeAppAlbumCustomPrintConfiguratorComplete,
} from '../../../native-bridge/native-apps-interface';
import {
  CUSTOM_PRINT_SUBTYPE,
  CUSTOM_PRINT_TYPE,
} from '../../../../constants/pro-custom-prints';
import getLogger from '../../../../util/logger';
import { MeWithRosterIdentity } from '../../../../graphql/queries/account';
import { Photo as PhotoQuery } from '../../../../graphql/queries/albums';
import { RosterIdentityUpdate as RosterIdentityUpdateMutation } from '../../../../apps/selfie-station/graphql/mutations/identity';
import { accountCardPrint as accountCardPrintMutation } from '../../../cards/graphql/mutations/accountCard';

const log = getLogger('StandaloneAlbumCustomPrintConfigurator');

const StandaloneAlbumCustomPrintConfigurator = () => {
  const location = useLocation();
  const { albumCustomPrintId } = useParams();
  const { accountCardId, photoId, rosterIdentityId } = qs.parse(
    location.search,
  );
  const isNativeWebView = useIsNativeWebView();
  const [rosterIdentityUpdate] = useMutation(RosterIdentityUpdateMutation);
  const [accountCardPrint] = useMutation(accountCardPrintMutation);
  const { albumCustomPrint, loading } =
    useStandaloneAlbumCustomPrintConfigurator({ albumCustomPrintId });
  const { data: { photo } = {}, loading: loadingPhoto } = useQuery(PhotoQuery, {
    variables: {
      id: photoId,
    },
    skip: !photoId,
  });
  const {
    data: { me: meWithRosterIdentity } = {},
    loading: loadingRosterIdentity,
  } = useQuery(MeWithRosterIdentity, {
    variables: {
      roster_identity_id: rosterIdentityId,
    },
    skip: !rosterIdentityId,
  });

  const { custom_print: customPrint } = albumCustomPrint ?? {};

  const isCollage = useMemo(
    () =>
      customPrint?.type === CUSTOM_PRINT_TYPE.PRINT_PRODUCT &&
      customPrint?.subtype === CUSTOM_PRINT_SUBTYPE.COLLAGE,
    [albumCustomPrint],
  );

  const isIdCard = useMemo(
    () =>
      customPrint?.type === CUSTOM_PRINT_TYPE.PRINT_PRODUCT &&
      customPrint?.subtype === CUSTOM_PRINT_SUBTYPE.ID_CARD,
    [albumCustomPrint],
  );

  const buttonText = useMemo(() => {
    if (isCollage) {
      return 'Create my Camper Collage';
    }

    if (isIdCard) {
      return 'Save';
    }

    return undefined;
  }, [isCollage, isIdCard]);

  const title = useMemo(() => {
    if (isCollage) {
      return 'Design your collage!';
    }

    if (isIdCard) {
      return 'Design your ID card';
    }

    return undefined;
  }, [isCollage, isIdCard]);

  const rosterIdentity = useMemo(
    () => meWithRosterIdentity?.roster_identities?.records?.[0],
    [meWithRosterIdentity],
  );

  const identity = useMemo(() => {
    if (!rosterIdentity) {
      return undefined;
    }

    return rosterIdentity?.linked_identities?.records?.[0]?.identity;
  }, [rosterIdentity]);

  const rosterIdentityLink = useMemo(() => {
    if (!albumCustomPrint || !rosterIdentity) {
      return undefined;
    }

    if (!albumCustomPrint.album?.root_album_folder?.id) {
      return undefined;
    }

    return rosterIdentity.links.records.find(
      link =>
        link?.album_folder?.id ===
        albumCustomPrint.album?.root_album_folder?.id,
    );
  }, [rosterIdentity, albumCustomPrint]);

  const onSaveIdCard = async accountCard => {
    if (!rosterIdentity || !rosterIdentityLink) {
      return;
    }

    try {
      await rosterIdentityUpdate({
        variables: {
          input: {
            roster_identity_id: rosterIdentity.id,
            update_links: [
              {
                roster_identity_link_id: rosterIdentityLink.id,
                id_card_account_card_id: accountCard.id,
              },
            ],
          },
        },
      });
    } catch {
      //   Do nothing. Should be a soft error.
    }
  };

  const onAccountCardPrint = async accountCardId => {
    try {
      const {
        data: { accountCardPrint: accountCardPrintResponse },
      } = await accountCardPrint({
        variables: {
          input: {
            account_card_id: accountCardId,
          },
        },
      });

      if (accountCardPrintResponse?.success) {
        return accountCardPrintResponse.account_card;
      }
    } catch {
      //   Do nothing. Should be a soft error..
    }
  };

  if (!albumCustomPrint || loading || loadingRosterIdentity || loadingPhoto) {
    return <FullScreenLoader />;
  }

  return (
    <AccountCardAlbumCustomPrintConfigurator
      onClose={
        isNativeWebView
          ? () => {
              postNativeAppAlbumCustomPrintConfiguratorDismiss();
            }
          : undefined
      }
      onFinished={async ({ accountCard }) => {
        log.info('onComplete :: ', {
          accountCard,
          albumCustomPrint,
        });

        let finalAccountCard = accountCard;
        if (isIdCard) {
          const accountCardWithPrintData = await onAccountCardPrint(
            accountCard.id,
          );
          finalAccountCard = accountCardWithPrintData;

          if (rosterIdentityLink) {
            await onSaveIdCard(accountCard);
          }
        }

        if (isNativeWebView) {
          postNativeAppAlbumCustomPrintConfiguratorComplete({
            accountCard: finalAccountCard ?? accountCard,
            albumCustomPrint,
          });
        }
      }}
      isAllPhotos
      albumCustomPrint={albumCustomPrint}
      albumMembershipGroup={{
        is_album: true,
        id: albumCustomPrint.album?.id,
        album: albumCustomPrint.album,
      }}
      defaultAccountCard={accountCardId ? { id: accountCardId } : undefined}
      buttonText={buttonText}
      forceEntitlement={isCollage || isIdCard}
      title={title}
      inlineButton={isCollage}
      altClassName={
        isCollage || isIdCard
          ? 'event-collage-option-account-card-configurator-modal'
          : undefined
      }
      photo={photo}
      identity={identity}
      rosterIdentityLink={rosterIdentityLink}
    />
  );
};

export default StandaloneAlbumCustomPrintConfigurator;
